import type { BetMarket } from '../types';

const PST = 'America/Los_Angeles';

/** Returns today's date as yyyy-MM-dd in Pacific time (PST/PDT). */
export function todayPST(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: PST });
}

export function formatOdds(american: number): string {
  return american > 0 ? `+${american}` : `${american}`;
}

export function formatMarket(market: BetMarket): string {
  const map: Record<BetMarket, string> = {
    moneyline:            'Moneyline',
    runline:              'Run Line',
    spread:               'Spread',
    total:                'Total',
    team_total_over:      'Team Total O',
    team_total_under:     'Team Total U',
    first5_moneyline:     'F5 Moneyline',
    first5_total:         'F5 Total',
    first5_runline:       'F5 Run Line',
    first_half_moneyline: 'H1 Moneyline',
    first_half_spread:    'H1 Spread',
    first_half_total:     'H1 Total',
    live_moneyline:       'Live ML',
    live_total:           'Live Total',
    player_prop:          'Player Prop',
    player_points:        'Player PTS',
    player_assists:       'Player AST',
    player_rebounds:      'Player REB',
    player_pra:           'Player PRA',
  };
  return map[market] ?? market;
}

export function formatPct(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

export function formatCurrency(value: number): string {
  return `$${Math.abs(value).toFixed(2)}`;
}

export function formatPnl(pnl: number): string {
  return pnl >= 0 ? `+$${pnl.toFixed(2)}` : `-$${Math.abs(pnl).toFixed(2)}`;
}

export function formatROI(roi: number): string {
  return roi >= 0 ? `+${(roi * 100).toFixed(1)}%` : `${(roi * 100).toFixed(1)}%`;
}

export function formatGameTime(gameDate: string): string {
  const d = new Date(gameDate);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: PST,
  });
}

export function formatDateShort(dateStr: string): string {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: PST,
  });
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'won':
      return 'text-win';
    case 'lost':
      return 'text-loss';
    case 'push':
      return 'text-gray-400';
    case 'placed':
    case 'live':
      return 'text-brand';
    case 'recommended':
      return 'text-purple-400';
    case 'void':
      return 'text-gray-500';
    default:
      return 'text-gray-400';
  }
}

export function getBetOutlook(
  bet: { selection: string; awayTeam: string },
  game: {
    linescore?: { awayScore?: number; homeScore?: number };
    awayScore?: number;
    homeScore?: number;
  },
): 'winning' | 'losing' | 'tied' {
  const awayScore = game.linescore?.awayScore ?? game.awayScore ?? 0;
  const homeScore = game.linescore?.homeScore ?? game.homeScore ?? 0;

  const selectionIsAway =
    bet.selection === bet.awayTeam ||
    bet.awayTeam.toLowerCase().includes(bet.selection.toLowerCase()) ||
    bet.selection.toLowerCase().includes(bet.awayTeam.toLowerCase().split(' ').pop()!);

  const myScore = selectionIsAway ? awayScore : homeScore;
  const theirScore = selectionIsAway ? homeScore : awayScore;

  if (myScore > theirScore) return 'winning';
  if (myScore < theirScore) return 'losing';
  return 'tied';
}

export function getEdgeClass(edge: number): string {
  if (edge >= 0.07) return 'edge-high';
  if (edge >= 0.04) return 'edge-medium';
  return 'edge-low';
}

const MS_PER_MINUTE = 60_000;

export function getTimeSensitivity(freshnessMs: number | undefined): {
  label: 'HIGH' | 'MODERATE' | 'LOW' | 'UNKNOWN';
  detail: string;
  cls: string;
} {
  if (freshnessMs === undefined)
    return { label: 'UNKNOWN', detail: 'Odds age unavailable', cls: 'text-gray-500' };
  const minutes = freshnessMs / MS_PER_MINUTE;
  if (minutes < 30) return { label: 'HIGH', detail: 'Act now — odds are fresh', cls: 'text-win' };
  if (minutes < 90)
    return { label: 'MODERATE', detail: 'Act soon — odds shifting', cls: 'text-warn' };
  return { label: 'LOW', detail: 'Verify odds before placing', cls: 'text-loss' };
}

/** Returns a synthesized bet-timing verdict combining odds freshness, game time, and steam. */
export function getTimingVerdict(
  oddsFreshnessMs: number | undefined,
  gameTimeIso: string | undefined,
  steamDetected: boolean,
): { text: string; cls: string; bgCls: string } | null {
  if (!gameTimeIso) return null;
  const minutesUntilGame = (new Date(gameTimeIso).getTime() - Date.now()) / MS_PER_MINUTE;
  if (minutesUntilGame <= 0) return null;

  const hoursUntilGame = minutesUntilGame / 60;
  const stale = oddsFreshnessMs !== undefined && oddsFreshnessMs > 180 * MS_PER_MINUTE;
  const aging = !stale && oddsFreshnessMs !== undefined && oddsFreshnessMs > 30 * MS_PER_MINUTE;
  const fresh = !stale && !aging;

  if (steamDetected)
    return { text: 'Steam moving this line — place before it shifts further', cls: 'text-warn', bgCls: 'bg-warn/10' };
  if (stale)
    return { text: 'Refresh odds before placing — data is stale', cls: 'text-loss', bgCls: 'bg-loss/10' };
  if (fresh && hoursUntilGame <= 2)
    return { text: 'Bet now — fresh odds and game window both closing', cls: 'text-win', bgCls: 'bg-win/10' };
  if (aging && hoursUntilGame <= 2)
    return { text: 'Act fast — odds aging and game approaching', cls: 'text-warn', bgCls: 'bg-warn/10' };
  if (aging && hoursUntilGame > 4)
    return { text: 'Wait — check for fresher odds before placing', cls: 'text-gray-400', bgCls: 'bg-surface-2' };
  if (fresh && hoursUntilGame > 4)
    return { text: 'Line stable — safe to wait, reassess in 2h', cls: 'text-brand-light', bgCls: 'bg-brand/5' };
  // 2–4h window, fresh odds
  return { text: 'Odds fresh — monitor for line movement before placing', cls: 'text-brand-light', bgCls: 'bg-brand/5' };
}

/** Returns a human-readable countdown until game time for the game card. */
export function formatCountdown(gameTimeIso: string | undefined): {
  text: string;
  cls: string;
} | null {
  if (!gameTimeIso) return null;
  const minutesUntilGame = (new Date(gameTimeIso).getTime() - Date.now()) / MS_PER_MINUTE;
  if (minutesUntilGame <= 0 || minutesUntilGame > 180) return null;

  if (minutesUntilGame <= 30) {
    return { text: `${Math.round(minutesUntilGame)}m`, cls: 'text-loss' };
  }
  const h = Math.floor(minutesUntilGame / 60);
  const m = Math.round(minutesUntilGame % 60);
  return { text: `${h}h ${m}m`, cls: 'text-warn' };
}
